<template>
  <div>
    <app-page-shell
      title="Error Tickets"
      subtitle="Production errors captured from backend and client workflows."
    >
      <app-list-toolbar
        :per-page="perPage"
        :per-page-options="perPageOptions"
        @update:perPage="perPage = $event"
      >
        <template #filters>
          <v-select
            v-model="statusFilter"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="statusOptions"
            :reduce="option => option.value"
            label="label"
            placeholder="All Status"
            class="status-filter-selector"
          />
        </template>

        <template #search>
          <b-form-input
            v-model="searchQuery"
            class="app-toolbar-search"
            placeholder="Search message / endpoint..."
          />
        </template>
      </app-list-toolbar>

      <b-table
        ref="refTicketListTable"
        class="position-relative"
        :items="fetchTickets"
        responsive
        :fields="tableColumns"
        primary-key="error_ticket_id"
        show-empty
        empty-text="No matching records found"
        :busy="loading"
      >
        <template #table-busy>
          <b-skeleton-table
            :rows="perPage || 5"
            :columns="tableColumns ? tableColumns.length : 9"
            :table-props="{ bordered: true, striped: true }"
          />
        </template>

        <template #cell(ticket_number)="data">
          <b-link @click="openDetail(data.item)">
            <span class="font-weight-bold">{{ data.item.ticket_number }}</span>
          </b-link>
        </template>

        <template #cell(function)="data">
          <div class="text-nowrap">
            <span class="align-text-top">{{ data.item.error_ticket_function }}</span>
          </div>
        </template>

        <template #cell(message)="data">
          <span
            class="text-truncate d-inline-block"
            style="max-width: 320px;"
          >
            {{ data.item.error_ticket_message }}
          </span>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="resolveTicketStatusVariant(data.item.error_ticket_status)">
            {{ resolveTicketStatusLabel(data.item.error_ticket_status) }}
          </b-badge>
        </template>

        <template #cell(user)="data">
          <span>{{ data.item.error_ticket_user_id || '-' }}</span>
        </template>

        <template #cell(branch)="data">
          <span>{{ data.item.error_ticket_branch_id || '-' }}</span>
        </template>

        <template #cell(time)="data">
          <span class="text-nowrap">{{ formatTime(data.item.error_ticket_created_time) }}</span>
        </template>

        <template #cell(source)="data">
          <b-badge variant="light-secondary">
            {{ data.item.error_ticket_source }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-button
            variant="link"
            size="sm"
            @click="openDetail(data.item)"
          >
            <feather-icon icon="EyeIcon" />
            <span class="align-middle ml-50">Detail</span>
          </b-button>
        </template>
      </b-table>

      <app-pagination-footer
        :meta="dataMeta"
        :current-page="currentPage"
        :total-rows="totalTickets"
        :per-page="perPage"
        @update:currentPage="currentPage = $event"
      />
    </app-page-shell>

    <b-modal
      id="ticket-detail-modal"
      v-model="isDetailModalActive"
      title="Ticket Detail"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="selectedTicket">
        <b-row>
          <b-col md="6">
            <p class="mb-25">
              <strong>Ticket Number</strong>
            </p>
            <p>{{ selectedTicket.ticket_number }}</p>
          </b-col>
          <b-col md="6">
            <p class="mb-25">
              <strong>Status</strong>
            </p>
            <b-badge :variant="resolveTicketStatusVariant(selectedTicket.error_ticket_status)">
              {{ resolveTicketStatusLabel(selectedTicket.error_ticket_status) }}
            </b-badge>
          </b-col>
          <b-col
            md="6"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>Source</strong>
            </p>
            <p>{{ selectedTicket.error_ticket_source }}</p>
          </b-col>
          <b-col
            md="6"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>Endpoint / Function</strong>
            </p>
            <p>{{ selectedTicket.error_ticket_function }}</p>
          </b-col>
          <b-col
            md="6"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>HTTP Status</strong>
            </p>
            <p>{{ selectedTicket.error_ticket_http_status || '-' }}</p>
          </b-col>
          <b-col
            md="6"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>Time</strong>
            </p>
            <p>{{ formatTime(selectedTicket.error_ticket_created_time) }}</p>
          </b-col>
          <b-col
            md="6"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>User ID</strong>
            </p>
            <p>{{ selectedTicket.error_ticket_user_id || '-' }}</p>
          </b-col>
          <b-col
            md="6"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>Branch ID</strong>
            </p>
            <p>{{ selectedTicket.error_ticket_branch_id || '-' }}</p>
          </b-col>

          <b-col
            cols="12"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>Message</strong>
            </p>
            <p>{{ selectedTicket.error_ticket_message }}</p>
          </b-col>

          <b-col
            v-if="selectedTicket.error_ticket_stack"
            cols="12"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>Stack Trace</strong>
            </p>
            <pre class="ticket-code-block">{{ selectedTicket.error_ticket_stack }}</pre>
          </b-col>

          <b-col
            cols="12"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>Payload</strong>
            </p>
            <pre class="ticket-code-block">{{ prettyJson(selectedTicket.error_ticket_payload) }}</pre>
          </b-col>

          <b-col
            cols="12"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>Response Body</strong>
            </p>
            <pre class="ticket-code-block">{{ prettyJson(selectedTicket.error_ticket_response_body) }}</pre>
          </b-col>

          <b-col
            v-if="selectedTicket.error_ticket_resolution_note"
            cols="12"
            class="mt-1"
          >
            <p class="mb-25">
              <strong>Resolution Note</strong>
            </p>
            <p>{{ selectedTicket.error_ticket_resolution_note }}</p>
          </b-col>
        </b-row>

        <hr>

        <b-row>
          <b-col md="6">
            <b-form-group label="Change Status">
              <v-select
                v-model="statusUpdate"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="statusUpdateOptions"
                :reduce="option => option.value"
                label="label"
                :clearable="false"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Resolution Note (optional)">
              <b-form-textarea
                v-model="resolutionNote"
                rows="3"
                placeholder="Add a note about this resolution..."
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end mt-1">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="isDetailModalActive = false"
          >
            Close
          </b-button>
          <b-button
            variant="primary"
            :disabled="isSavingStatus"
            @click="saveStatus"
          >
            Save Status
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BFormInput, BFormGroup, BFormTextarea, BButton, BTable, BLink,
  BBadge, BModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import moment from 'moment-timezone'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref } from '@vue/composition-api'
import AppPageShell from '@/views/apps/components/AppPageShell.vue'
import AppListToolbar from '@/views/apps/components/AppListToolbar.vue'
import AppPaginationFooter from '@/views/apps/components/AppPaginationFooter.vue'

import useTicketsList from './TicketsList'

export default {
  components: {
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BFormTextarea,
    BButton,
    BTable,
    BLink,
    BBadge,
    BModal,
    vSelect,
    AppPageShell,
    AppListToolbar,
    AppPaginationFooter,
  },
  setup() {
    const {
      fetchTickets,
      updateTicketStatus,
      loading,
      tableColumns,
      perPage,
      currentPage,
      totalTickets,
      dataMeta,
      perPageOptions,
      searchQuery,
      refTicketListTable,
      refetchData,
      statusFilter,
      resolveTicketStatusVariant,
      resolveTicketStatusLabel,
    } = useTicketsList()

    const statusOptions = [
      { label: 'All Status', value: null },
      { label: 'Open', value: 'open' },
      { label: 'In Progress', value: 'in_progress' },
      { label: 'Resolved', value: 'resolved' },
      { label: 'Ignored', value: 'ignored' },
    ]

    const statusUpdateOptions = [
      { label: 'Open', value: 'open' },
      { label: 'In Progress', value: 'in_progress' },
      { label: 'Resolved', value: 'resolved' },
      { label: 'Ignored', value: 'ignored' },
    ]

    const isDetailModalActive = ref(false)
    const selectedTicket = ref(null)
    const statusUpdate = ref('open')
    const resolutionNote = ref('')
    const isSavingStatus = ref(false)

    return {
      fetchTickets,
      updateTicketStatus,
      loading,
      tableColumns,
      perPage,
      currentPage,
      totalTickets,
      dataMeta,
      perPageOptions,
      searchQuery,
      refTicketListTable,
      refetchData,
      statusFilter,
      resolveTicketStatusVariant,
      resolveTicketStatusLabel,
      statusOptions,
      statusUpdateOptions,
      isDetailModalActive,
      selectedTicket,
      statusUpdate,
      resolutionNote,
      isSavingStatus,
    }
  },
  methods: {
    openDetail(item) {
      this.selectedTicket = item
      this.statusUpdate = item.error_ticket_status || 'open'
      this.resolutionNote = item.error_ticket_resolution_note || ''
      this.isDetailModalActive = true
    },
    formatTime(value) {
      if (!value) return '-'
      return moment(value).format('DD MMM YYYY HH:mm')
    },
    prettyJson(value) {
      if (!value) return '-'
      try {
        return JSON.stringify(JSON.parse(value), null, 2)
      } catch (e) {
        return value
      }
    },
    saveStatus() {
      if (!this.selectedTicket) return

      this.isSavingStatus = true
      this.updateTicketStatus(this.selectedTicket.error_ticket_id, this.statusUpdate, this.resolutionNote)
        .then(response => {
          this.isSavingStatus = false
          if (response.data && response.data.success === true) {
            this.isDetailModalActive = false
            this.refetchData()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Success',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Ticket status has been updated!',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Notification',
                icon: 'BellIcon',
                variant: 'danger',
                text: (response.data && response.data.message) || 'Something went wrong',
              },
            })
          }
        })
        .catch(error => {
          console.error(error)
          this.isSavingStatus = false
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: 'Something went wrong',
            },
          })
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.status-filter-selector {
  width: 180px;
}
.app-toolbar-search {
  max-width: 340px;
}
.ticket-code-block {
  background-color: #f8f8f8;
  padding: 0.75rem;
  border-radius: 0.357rem;
  max-height: 260px;
  overflow: auto;
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 991.98px) {
  .status-filter-selector,
  .app-toolbar-search {
    width: 100%;
    max-width: none;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
